import { Component, ViewEncapsulation, inject, OnInit, OnDestroy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MCBarSkeletonComponent, MCButtonComponent } from '@maslul/common-components';
import { Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MCTranslationStateService } from '@maslul/common-services';
import { PageBaseComponent } from '../../shared/page-base';
import { RequestService } from '../../../services/request/request';
import { OpenRequestFromLinkService } from '../../../services/request/open-request-from-link';
import { FieldsFormStateService } from '../../../services/fields/form-state';
import { AuthStateService, AuthStatus } from '../../../services/guards/auth-state.service';

@Component({
    selector: 'mi-home',
    templateUrl: './home.html',
    styleUrl: './home.scss',
    standalone: true,
    imports: [
        MCButtonComponent,
        MCBarSkeletonComponent
    ],
    encapsulation: ViewEncapsulation.None
})
export class HomeComponent extends PageBaseComponent implements OnInit, OnDestroy {

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private translateService = inject(TranslateService);
    private translationStateService = inject(MCTranslationStateService);
    private fieldsFormStateService = inject(FieldsFormStateService);
    private openRequestFromLinkService = inject(OpenRequestFromLinkService);
    private requestService = inject(RequestService);
    private authStateService = inject(AuthStateService);

    private subscription = new Subscription();
    private dataLoaded$ = new BehaviorSubject<boolean>(false);

    genericText = toSignal(this.translateService.stream('generic'));
    translationsLoaded = toSignal(this.translationStateService.translationsLoaded$);

    isLoading = toSignal(
        combineLatest([
            this.translationStateService.translationsLoaded$,
            this.dataLoaded$
        ]).pipe(
            map(([translationsLoaded, dataLoaded]) => !(translationsLoaded && dataLoaded))
        )
    );

    approvalTypeKey: string | null = "";
    hasSecondRequest = false;
    isApprovalTypeFullyResolved = false;

    ngOnInit() {
        this.checkQueryParams();
        this.initRequestData();
    }

    private initRequestData() {
        if (!this.isNewRequest && this.requestId) {
            this.waitForAuthThenLoadRequest();
        } else {
            this.dataLoaded$.next(true);
        }
    }

    private waitForAuthThenLoadRequest() {
        this.subscription.add(
            this.authStateService.getAuthStatus().pipe(
                tap((status: AuthStatus) => console.log('[Home] authStatus received:', status)),
                filter((status: AuthStatus) => status !== 'idle' && status !== 'authenticating'),
                take(1)
            ).subscribe((status: AuthStatus) => {
                console.log('[Home] auth resolved with status:', status);
                if (status === 'unauthorized') {
                    this.router.navigate(['/unauthorized']);
                } else if (status === 'error') {
                    this.router.navigate(['/error']);
                } else {
                    this.loadRequestDataForExistingRequest();
                }
            })
        );
    }

    private loadRequestDataForExistingRequest() {
        if (this.requestId) {
            this.subscription.add(
                this.requestService.loadExistingRequest(this.requestId).subscribe({
                    next: () => {
                        this.dataLoaded$.next(true);
                    },
                    error: (error: any) => {
                        console.error('שגיאה בטעינת הבקשה:', error);
                        this.navigate('error');
                    }
                })
            );
        }
    }

    checkQueryParams() {
        const key = this.route.snapshot.queryParamMap.get('approval-type');
        this.approvalTypeKey = key;
        if (key) {
            this.fieldsFormStateService.setFormState('isRequestByUrl', true);
            const { factor, code, second } =
                this.openRequestFromLinkService.extractApprovalTypeDetailsFromKey(key);
            this.hasSecondRequest = second !== '' && second !== '00';
            this.subscription.add(
                this.openRequestFromLinkService.updateApprovalTypeDetailsByApTypeKeys(
                    factor,
                    code,
                    second,
                ).subscribe((isFullyResolved: boolean) => {
                    this.isApprovalTypeFullyResolved = isFullyResolved;
                    if (isFullyResolved) {
                        this.requestService.initMyImportFormState();
                    }
                })
            );
        }
    }

    public override next(): void {
        if (this.approvalTypeKey && this.approvalTypeKey.length > 0) {
            if (!this.isApprovalTypeFullyResolved) {
                this.navigate("request-submission-methods");
            } else {
                this.hasSecondRequest ? this.navigate("applicant-details") : this.navigate("approval-type-method");
            }
        } else {
            this.navigate("request-submission-methods");
        }
    }

    public override back(): void {
        // מימוש במידת הצורך
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
